/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { AnyAction } from 'redux';
import { ServiceEndpoints } from 'client-code/sls-service-endpoints';
import { ApplicationStartup, Payload, SegmentTransport, VU } from '@tivocorp/tivo-webapp-loggers';
import { NetworkInterceptor } from '../../core/device/NetworkInterceptor';
import {
    APP_START,
    OVERRIDE_PLAYBACK_PARAMS,
    SET_SLS_GLOBAL_ENDPOINTS,
    IS_APPLICATION_READY,
    SET_PARTNER_BRANDING_IMAGE_URL,
} from './appl.types';
import { appStartAction, overridePlaybackParamsAction } from './appl.actions';
import { Log } from '../../core/logging/Log';
import { Locale } from '../../core/locale/Locale';
import { DeviceModel } from '../../core/device/DeviceModel';
import { Storage } from '../../core/storage/Storage';
import { QoEAppLogger } from '../../core/logging/qoe/QoEAppLogger';
import { DeviceFeatures } from '../../core/device/DeviceFeatures';
import { prepareForAppLaunch } from '../signin/presignin.reducers';
import { initiateSignin } from '../signin/signin.reducers';
import { launchHomeScreen } from '../signin/postsignin.reducers';
import { resetAntiburnTimeout } from '../antiburnosd/antiburnosd.reducers';
import { languageNotSupportedError } from '../error/erroroverlays.reducers';
import { Branding } from '../../core/branding/Branding';
import { UniqueIdentifier } from '../../core/utils/UniqueIdentifier';
import { VuLogger } from '../../core/logging';

const TAG = 'appl';

const initialState: {
    isOverrideEnable: boolean;
    overrideDetails: any;
    slsGlobalEndpoints: ServiceEndpoints[];
    isApplicationReady: boolean;
    partnerBrandingImageUrl: string;
} = {
    isOverrideEnable: false,
    overrideDetails: {},
    slsGlobalEndpoints: [],
    isApplicationReady: false,
    partnerBrandingImageUrl: '',
};

const applReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case APP_START:
            return state;

        // __START_DEBUG_CODE_CLEANUP__
        case OVERRIDE_PLAYBACK_PARAMS:
            return {
                ...state,
                isOverrideEnable: payload.isOverrideEnable,
                overrideDetails: payload.overrideDetails,
            };
        // __END_DEBUG_CODE_CLEANUP__

        case SET_SLS_GLOBAL_ENDPOINTS:
            return {
                ...state,
                slsGlobalEndpoints: payload.slsGlobalEndpoints,
            };
        case IS_APPLICATION_READY:
            return {
                ...state,
                isApplicationReady: payload.isApplicationReady,
            };

        case SET_PARTNER_BRANDING_IMAGE_URL:
            return {
                ...state,
                partnerBrandingImageUrl: payload.partnerBrandingImageUrl,
            };

        default:
            return state;
    }
};

// __START_DEBUG_CODE_CLEANUP__
const urlParams = new URLSearchParams(window.location.search);

const continueOverridePlaybackParams = () => (dispatch: Function) => {
    const tuningUri = urlParams.get('tuningUri');
    const drmUri = urlParams.get('drmUri');
    const errorCode = urlParams.get('errorCode');
    dispatch(
        overridePlaybackParamsAction({
            tuningUri,
            drmUri,
            errorCode,
        }),
    );
};
// __END_DEBUG_CODE_CLEANUP__

export const appStart = () => async (dispatch: Function) => {
    NetworkInterceptor.logging(DeviceFeatures.isServiceApiLoggingEnabled());
    // Application execution session identifier
    QoEAppLogger.createAppSessionId();

    ApplicationStartup.noConsole();
    ApplicationStartup.info(TAG, 'Application start.');
    Log.i(TAG, 'Application start.');
    dispatch(appStartAction());

    ApplicationStartup.info(TAG, 'Device global parameters:', DeviceModel.getGlobalParams());
    Log.i(TAG, 'Device global parameters:', DeviceModel.getGlobalParams());
    Log.i(TAG, 'Device Total Memory:', DeviceModel.getTVProperties().getDeviceTotalMemory());

    if (!DeviceModel.getTVProperties().hasSupportedLanguage()) {
        dispatch(languageNotSupportedError());
        return;
    }

    Locale.setLocale(
        DeviceModel.getTVProperties().getLocale(),
        DeviceModel.getTiVoSettings().getMsoName(),
        DeviceModel.getTVProperties().getPlatformName(),
    );

    // __START_DEBUG_CODE_CLEANUP__
    // TODO: Need to be handled with dynamic configuration to enable disable override feature.
    if (urlParams.get('tuningUri') || urlParams.get('errorCode')) {
        dispatch(continueOverridePlaybackParams());
    }

    const streamStats = urlParams.get('streamStats') === 'true';
    if (streamStats) {
        Storage.getInstance().setItem(Storage.Keys.IS_VIDEO_DEBUG_OVERLAY_ACTIVE, streamStats);
    } else {
        Storage.getInstance().removeItem(Storage.Keys.IS_VIDEO_DEBUG_OVERLAY_ACTIVE);
    }
    // __END_DEBUG_CODE_CLEANUP__

    dispatch(appRunning());
};
const initializeVuLogging = () => {
    const globalParam = {
        app: {
            name: DeviceModel.getAppName(),
            version: DeviceModel.getAppVersionName(),
        },
        applicationSessionId: UniqueIdentifier.uuid4(),
        device: {
            id: DeviceModel.getTVProperties().getMacAddress(),
            type: DeviceModel.getTiVoSettings().getDeviceType(),
            adTrackingEnabled: false,
        },
        locale: DeviceModel.getTVProperties().getLocale().replace('_', '-'),
        msoName: DeviceModel.getTiVoSettings().getMsoName(),
        timezone: DeviceModel.getTVProperties().getTimezone(),
    };
    const globalPayload: Payload = new Payload(globalParam);
    VuLogger.setGlobalParams(globalPayload);
    VU.noConsole();
    VU.getInstance().setTransport(
        new SegmentTransport({
            key: DeviceModel.getTiVoSettings().getSegmentTransportKey(),
        }),
    );
};

export const appRunning = () => async (dispatch: Function) => {
    Branding.setFallbackBrandings(
        DeviceModel.getTVProperties().getLocale(),
        DeviceModel.getTiVoSettings().getMsoName(),
    );
    initializeVuLogging();
    VuLogger.logAppEntryEvent();
    await dispatch(prepareForAppLaunch());
    await dispatch(initiateSignin());
    await dispatch(launchHomeScreen());
    dispatch(resetAntiburnTimeout());
};

export default applReducer;
